import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) return null;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[AuthContext] fetchProfile error:', error.message);
      return null;
    }
    setProfile(data);
    return data;
  }, []);

  useEffect(() => {
    let mounted = true;
    let safetyTimer;

    const init = async () => {
      // SAFETY NET: nunca deixar o loading travado mais de 4s
      safetyTimer = setTimeout(() => {
        if (mounted) {
          // eslint-disable-next-line no-console
          console.warn('[AuthContext] init safety timeout — destravando loading');
          setLoading(false);
        }
      }, 4000);

      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(s);
        setUser(s?.user || null);
        // Marca loading false ASSIM QUE a sessão estiver resolvida (não espera profile)
        // Isso evita travamento se fetchProfile demorar ou falhar.
        setLoading(false);
        if (s?.user) {
          // Fire-and-forget — não bloqueia o loading da app
          fetchProfile(s.user.id).catch((err) => {
            // eslint-disable-next-line no-console
            console.warn('[AuthContext] background fetchProfile failed:', err?.message);
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[AuthContext] init error:', e);
        if (mounted) setLoading(false);
      } finally {
        clearTimeout(safetyTimer);
      }
    };
    init();

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      setUser(newSession?.user || null);
      if (newSession?.user) {
        fetchProfile(newSession.user.id).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[AuthContext] onAuthStateChange fetchProfile failed:', err?.message);
        });
      } else {
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      sub?.subscription?.unsubscribe();
    };
  }, [fetchProfile]);

  const signUpWithEmail = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signInWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signInWithMagicLink = async (email) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw error;
  };

  const resetPasswordForEmail = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset`,
    });
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('Não autenticado');
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    setProfile(data);
    return data;
  };

  const refreshProfile = async () => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signUpWithEmail,
    signInWithEmail,
    signInWithMagicLink,
    resetPasswordForEmail,
    signInWithGoogle,
    signOut,
    updateProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
};
